import { Fragment, useMemo, useRef, type ReactNode, type RefObject } from "react";
import type { AgentEvent } from "../../../lib/types";
import { cn } from "../../../lib/cn";
import { ArtifactCard } from "./ArtifactCard";
import { ToolCallCard } from "./ToolCallCard";
import {
  AssistantMessage,
  RunResultBlock,
  SessionStartBlock,
  StandaloneResultBlock,
} from "./blocks";
import { toolMeta } from "./toolMeta";

interface Props {
  events: AgentEvent[];
  /** Task id — resets the "what's already here" baseline on task switch. */
  taskKey: string | null;
  highlightOrdinal: number | null;
  /** Attached to the last rendered item so the viewer can scroll it in. */
  lastEventRef: RefObject<HTMLDivElement>;
}

type Item = { ordinal: number; node: ReactNode };
type Group =
  | { id: number; kind: "standalone"; item: Item }
  | { id: number; kind: "turn"; header: ReactNode | null; headerOrdinal: number; items: Item[] };

/**
 * Renders the merged event stream as a grouped timeline. Tool calls are paired
 * with their results into one card; an assistant message opens a "turn" and the
 * tool cards that follow indent under it along a left spine. Every event is
 * rendered exactly once — a result with no matching call still shows as its own
 * card, nothing is dropped. Only events that arrive after first paint animate
 * in, so opening a long finished log doesn't cascade.
 */
export function ActivityTimeline({
  events,
  taskKey,
  highlightOrdinal,
  lastEventRef,
}: Props) {
  const groups = useMemo(() => buildGroups(events), [events]);
  const lastOrdinal = events.length ? events[events.length - 1].ordinal : -1;

  // Suppress the entry animation for the historical backfill: lock a baseline
  // to the first batch's max ordinal, then animate only higher (live) ones.
  const baselineRef = useRef<{ key: string | null; max: number }>({
    key: null,
    max: Infinity,
  });
  if (baselineRef.current.key !== taskKey) {
    baselineRef.current = { key: taskKey, max: Infinity };
  }
  if (baselineRef.current.max === Infinity && events.length > 0) {
    baselineRef.current.max = lastOrdinal;
  }
  const baselineMax = baselineRef.current.max;

  const renderItem = (item: Item) => (
    <TimelineItem
      key={item.ordinal}
      item={item}
      isLast={item.ordinal === lastOrdinal}
      isHighlight={item.ordinal === highlightOrdinal}
      animate={item.ordinal > baselineMax}
      lastRef={lastEventRef}
    />
  );

  return (
    <div className="space-y-3">
      {groups.map((g) =>
        g.kind === "standalone" ? (
          <Fragment key={g.id}>{renderItem(g.item)}</Fragment>
        ) : (
          <div key={g.id} className="space-y-2">
            {g.header &&
              renderItem({ ordinal: g.headerOrdinal, node: g.header })}
            {g.items.length > 0 && (
              <div
                className={cn(
                  "space-y-2",
                  g.header && "ml-3 border-l border-border-subtle pl-3",
                )}
              >
                {g.items.map(renderItem)}
              </div>
            )}
          </div>
        ),
      )}
    </div>
  );
}

function TimelineItem({
  item,
  isLast,
  isHighlight,
  animate,
  lastRef,
}: {
  item: Item;
  isLast: boolean;
  isHighlight: boolean;
  animate: boolean;
  lastRef: RefObject<HTMLDivElement>;
}) {
  return (
    <div
      ref={isLast ? lastRef : undefined}
      className={cn(
        "rounded-md transition-colors duration-slow ease-out",
        animate && "activity-in",
        isHighlight && "bg-accent-soft/60",
      )}
    >
      {item.node}
    </div>
  );
}

function buildGroups(events: AgentEvent[]): Group[] {
  // First pass: which tool_use ids have a call, and the result per id.
  const callIds = new Set<string>();
  const resultByToolId = new Map<string, AgentEvent>();
  for (const ev of events) {
    if (ev.kind === "tool_call") {
      const id = String(ev.payload.tool_use_id ?? "");
      if (id) callIds.add(id);
    } else if (ev.kind === "tool_result") {
      const id = String(ev.payload.tool_use_id ?? "");
      if (id) resultByToolId.set(id, ev);
    }
  }

  const groups: Group[] = [];
  let turn: Extract<Group, { kind: "turn" }> | null = null;
  const flush = () => {
    if (turn) {
      groups.push(turn);
      turn = null;
    }
  };
  const ensureTurn = (
    ordinal: number,
  ): Extract<Group, { kind: "turn" }> => {
    if (turn) return turn;
    const t: Extract<Group, { kind: "turn" }> = {
      id: ordinal,
      kind: "turn",
      header: null,
      headerOrdinal: ordinal,
      items: [],
    };
    turn = t;
    return t;
  };

  for (const ev of events) {
    switch (ev.kind) {
      case "session_start":
        flush();
        groups.push({
          id: ev.ordinal,
          kind: "standalone",
          item: { ordinal: ev.ordinal, node: <SessionStartBlock event={ev} /> },
        });
        break;
      case "text": {
        const text = String(ev.payload.text ?? "");
        if (!text.trim()) break; // empty narration — nothing to show
        flush();
        turn = {
          id: ev.ordinal,
          kind: "turn",
          header: <AssistantMessage text={text} />,
          headerOrdinal: ev.ordinal,
          items: [],
        };
        break;
      }
      case "tool_call": {
        const name = String(ev.payload.name ?? "tool");
        const id = String(ev.payload.tool_use_id ?? "");
        const result = id ? resultByToolId.get(id) : undefined;
        const node = toolMeta(name).isArtifact ? (
          <ArtifactCard event={ev} result={result} />
        ) : (
          <ToolCallCard event={ev} result={result} />
        );
        ensureTurn(ev.ordinal).items.push({ ordinal: ev.ordinal, node });
        break;
      }
      case "tool_result": {
        const id = String(ev.payload.tool_use_id ?? "");
        if (id && callIds.has(id)) break; // shown inside its call's card
        ensureTurn(ev.ordinal).items.push({
          ordinal: ev.ordinal,
          node: <StandaloneResultBlock event={ev} />,
        });
        break;
      }
      case "result":
        flush();
        groups.push({
          id: ev.ordinal,
          kind: "standalone",
          item: { ordinal: ev.ordinal, node: <RunResultBlock event={ev} /> },
        });
        break;
      default:
        break; // usage rolls into the header cost stamp
    }
  }
  flush();
  return groups;
}
